// src/pages/demo/MBSDemoPage.js
import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Backdrop,
  Fade,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  PlayArrow, 
  Pause, 
  VolumeUp,
  Language,
  CheckCircle,
  Business,
  Public,
  Close,
  ArrowBack,
  Translate,
  RecordVoiceOver,
  Psychology,
  EmojiEvents
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../utils/color';
import useSEO from '../../hooks/useSEO';

// Styled Components with Saudi theme colors
const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, #006C35 0%, #004d26 100%)`, // Saudi green gradient
  padding: theme.spacing(6, 0, 2, 0),
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, transparent 70%)`,
    pointerEvents: 'none',
  },
}));

const VideoCard = styled(Card)(({ theme, isOriginal }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  border: isOriginal 
    ? `2px solid #006C35` 
    : `1px solid ${COLORS.border.primary}`,
  background: `linear-gradient(135deg, ${COLORS.background.primary} 0%, ${COLORS.background.secondary} 100%)`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 24px rgba(0,108,53,0.3)`,
  },
}));

const VideoContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  paddingTop: '56.25%', // 16:9 aspect ratio
  backgroundColor: '#000',
  borderRadius: '8px',
  overflow: 'hidden',
});

const VideoPlaceholder = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  color: 'white',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
}));

const VideoModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
}));

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '90vw',
  maxWidth: '1200px',
  backgroundColor: COLORS.background.primary,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  outline: 'none',
  border: `2px solid #006C35`,
  boxShadow: `0 20px 40px rgba(0,108,53,0.4)`,
}));

const ModalVideo = styled('video')(({ theme }) => ({
  width: '100%',
  height: 'auto',
  maxHeight: '70vh',
  borderRadius: theme.spacing(1),
  backgroundColor: '#000',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: `${COLORS.background.secondary}90`,
  color: COLORS.text.primary,
  zIndex: 1,
  '&:hover': {
    backgroundColor: COLORS.background.secondary,
  },
}));

const LanguageFlag = styled(Box)({
  fontSize: '2rem',
  marginBottom: '8px',
});

const PlayButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: `rgba(0,108,53,0.8)`,
  color: 'white',
  width: 60,
  height: 60,
  marginBottom: theme.spacing(2),
  '&:hover': {
    backgroundColor: '#006C35',
    transform: 'scale(1.1)',
  },
}));

const ContextCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, rgba(0,108,53,0.05) 0%, rgba(0,77,38,0.05) 100%)`,
  border: `1px solid rgba(0,108,53,0.2)`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
}));

const MBSDemoPage = () => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  
  useSEO({
    title: 'MBS Speech AI Dubbing Demo - Saudi Crown Prince | DubLabs',
    description: 'Experience DubLabs AI video dubbing with Crown Prince Mohammed bin Salman speeches — perfect lip-sync translation across multiple diplomatic languages.',
    keywords: 'MBS speech dubbing, Saudi Arabia video translation, diplomatic content dubbing, Arabic to English dubbing, Vision 2030 multilingual'
  });

  // Demo videos for MBS speeches
  const demoVideos = [
    {
      id: 'original',
      title: 'Original Speech',
      language: 'English',
      flag: '🇬🇧',
      speakers: 'Original',
      description: 'Vision 2030 presentation in English',
      isOriginal: true,
      videoUrl: '/demo/mbs/original-english.mp4', // You'll need to add these videos
      features: ['Original Voice', 'Clear Audio', 'Professional Delivery'],
      context: 'Future Investment Initiative Conference'
    },
    {
      id: 'arabic',
      title: 'Arabic Version',
      language: 'العربية',
      flag: '🇸🇦',
      speakers: '400M+ speakers',
      description: 'AI-dubbed with perfect Arabic pronunciation',
      videoUrl: '/demo/mbs/arabic.mp4',
      features: ['Native Accent', 'Cultural Nuances', 'Perfect Lip-Sync'],
      context: 'For Middle Eastern audiences'
    },
    {
      id: 'spanish',
      title: 'Spanish Version',
      language: 'Español',
      flag: '🇪🇸',
      speakers: '500M+ speakers',
      description: 'AI-dubbed for Spanish-speaking markets',
      videoUrl: '/demo/mbs/spanish.mp4',
      features: ['Natural Flow', 'Voice Preservation', 'Emotional Tone'],
      context: 'For Latin American partnerships'
    },
    {
      id: 'french',
      title: 'French Version', 
      language: 'Français',
      flag: '🇫🇷',
      speakers: '280M+ speakers',
      description: 'AI-dubbed for French-speaking regions',
      videoUrl: '/demo/mbs/french.mp4',
      features: ['Diplomatic Tone', 'Professional Quality', 'Accurate Translation'],
      context: 'For European and African markets'
    },
    {
      id: 'mandarin',
      title: 'Mandarin Version',
      language: '中文',
      flag: '🇨🇳', 
      speakers: '900M+ speakers',
      description: 'AI-dubbed for Chinese business partners',
      videoUrl: '/demo/mbs/mandarin.mp4',
      features: ['Tonal Accuracy', 'Business Context', 'Cultural Adaptation'],
      context: 'For Asian market expansion'
    },
    {
      id: 'japanese',
      title: 'Japanese Version',
      language: '日本語',
      flag: '🇯🇵',
      speakers: '125M+ speakers', 
      description: 'AI-dubbed for Japanese investors',
      videoUrl: '/demo/mbs/japanese.mp4',
      features: ['Formal Register', 'Respectful Tone', 'Precise Translation'],
      context: 'For Japanese business relations'
    }
  ];

  const useCases = [
    {
      icon: <Business sx={{ fontSize: 30, color: '#006C35' }} />,
      title: 'Investment Conferences',
      description: 'Reach global investors in their native languages'
    },
    {
      icon: <Public sx={{ fontSize: 30, color: '#004d26' }} />,
      title: 'International Relations',
      description: 'Diplomatic communications with perfect cultural context'
    },
    {
      icon: <Translate sx={{ fontSize: 30, color: '#006C35' }} />,
      title: 'Vision 2030 Outreach',
      description: 'Share Saudi Vision globally in 32 languages'
    },
    {
      icon: <EmojiEvents sx={{ fontSize: 30, color: '#004d26' }} />,
      title: 'NEOM Presentations',
      description: 'Showcase mega-projects to international stakeholders'
    }
  ];

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedVideo(null);
  };

  const handleGetStarted = () => {
    navigate('/contact');
  };

  const handleBackToDemo = () => {
    navigate('/demo');
  };

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToDemo}
            sx={{
              color: 'white',
              mb: 3,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              }
            }}
          >
            Back to Main Demo
          </Button>
          
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: 'white',
                mb: 2,
              }}
            >
              🇸🇦 Saudi Leadership Voice
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 600,
                mb: 3,
                fontSize: { xs: '1.2rem', md: '1.8rem' }
              }}
            >
              Crown Prince MBS Speeches in Multiple Languages
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                fontSize: '1.1rem',
                lineHeight: 1.6,
                mb: 4,
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              Experience how AI dubbing technology can transform diplomatic and business 
              communications. See Vision 2030 presentations perfectly translated while 
              maintaining the speaker's authority and emotional resonance.
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
              <Chip 
                icon={<RecordVoiceOver />}
                label="Voice Preserved" 
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} 
              />
              <Chip 
                icon={<Psychology />}
                label="Cultural Context" 
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} 
              />
              <Chip 
                icon={<Language />}
                label="32 Languages"
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} 
              />
            </Box>
          </Box>
        </Container>
      </HeroSection>

      {/* Context Section */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ContextCard>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#006C35', mb: 2 }}>
            Diplomatic & Business Communication Excellence
          </Typography>
          <Typography variant="body1" sx={{ color: COLORS.text.secondary, lineHeight: 1.8 }}>
            This demonstration showcases how DubLabs' AI technology can be applied to high-level 
            diplomatic and business communications. Perfect for investment conferences, international 
            partnerships, and global outreach programs. Our technology ensures that the speaker's 
            authority, tone, and message integrity are perfectly preserved across all languages.
          </Typography>
        </ContextCard>
      </Container>

      {/* Video Demo Section */}
      <Container maxWidth="xl" sx={{ py: 1 }}>
        <Typography
          variant="h2"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            color: COLORS.text.primary,
            mb: 2,
            fontSize: { xs: '2rem', md: '2.5rem' }
          }}
        >
          Vision 2030: One Message, Global Reach
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            color: COLORS.text.secondary,
            mb: 6,
            fontSize: '1.1rem',
            maxWidth: 800,
            mx: 'auto'
          }}
        >
          Watch how the same Vision 2030 presentation resonates perfectly in multiple languages, 
          maintaining diplomatic precision and cultural sensitivity.
        </Typography>

        {/* Video Grid */}
        <Grid container spacing={3}>
          {demoVideos.map((video, index) => (
            <Grid item xs={12} md={6} lg={4} key={video.id}>
              <VideoCard isOriginal={video.isOriginal}>
                <VideoContainer>
                  <video
                    src={video.videoUrl}
                    preload="metadata"
                    muted
                    playsInline
                    style={{
                      position: 'absolute', top: 0, left: 0,
                      width: '100%', height: '100%',
                      objectFit: 'cover',
                      pointerEvents: 'none',
                    }}
                  />
                  <VideoPlaceholder onClick={() => handleVideoClick(video)}>
                    <LanguageFlag>{video.flag}</LanguageFlag>
                    <PlayButton>
                      <PlayArrow sx={{ fontSize: 30 }} />
                    </PlayButton>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {video.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Click to play
                    </Typography>
                  </VideoPlaceholder>
                </VideoContainer>
                
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.text.primary }}>
                      {video.language}
                    </Typography>
                    <Chip 
                      label={video.speakers} 
                      size="small"
                      sx={{ backgroundColor: `rgba(0,108,53,0.1)`, color: '#006C35' }} 
                    />
                  </Box>
                  
                  <Typography variant="body2" sx={{ color: COLORS.text.secondary, mb: 2 }}>
                    {video.description}
                  </Typography>
                  
                  <Typography variant="caption" sx={{ color: '#006C35', fontWeight: 600, display: 'block', mb: 2 }}>
                    Context: {video.context}
                  </Typography>
                  
                  <List dense sx={{ p: 0 }}>
                    {video.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckCircle sx={{ fontSize: 16, color: '#006C35' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature} 
                          primaryTypographyProps={{ 
                            variant: 'body2', 
                            sx: { color: COLORS.text.tertiary, fontSize: '0.9rem' } 
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </VideoCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Use Cases Section */}
      <Box sx={{ backgroundColor: COLORS.background.secondary, py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: COLORS.text.primary,
              mb: 6,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            Perfect for Saudi Business & Diplomacy
          </Typography>
          
          <Grid container spacing={4}>
            {useCases.map((useCase, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ 
                  height: '100%', 
                  background: `linear-gradient(135deg, ${COLORS.background.primary} 0%, ${COLORS.background.secondary} 100%)`,
                  border: `1px solid rgba(0,108,53,0.2)`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 25px rgba(0,108,53,0.2)`,
                  }
                }}>
                  <CardContent sx={{ p: 4, textAlign: 'left' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {useCase.icon}
                      <Typography variant="h6" sx={{ ml: 2, fontWeight: 600, color: COLORS.text.primary }}>
                        {useCase.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: COLORS.text.secondary, lineHeight: 1.6 }}>
                      {useCase.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Copyright Disclaimer */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ 
          p: 3, 
          background: `linear-gradient(135deg, rgba(0,108,53,0.05) 0%, rgba(0,77,38,0.05) 100%)`,
          border: `1px solid rgba(0,108,53,0.2)`,
          borderRadius: 2,
          textAlign: 'center'
        }}>
          <Typography variant="body2" sx={{ color: COLORS.text.secondary, fontStyle: 'italic' }}>
            Demo purposes only. All video content is for demonstration and does not represent actual speeches. 
            DubLabs respects all copyright and intellectual property rights.
          </Typography>
        </Paper>
      </Container>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Box sx={{ 
          background: `linear-gradient(135deg, #006C35, #004d26)`, 
          borderRadius: 3, 
          p: 6,
          color: 'white'
        }}>
          <Business sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            Transform Your Global Communications
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Perfect for government agencies, international businesses, and diplomatic missions
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleGetStarted}
            sx={{
              backgroundColor: 'white',
              color: '#006C35',
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.9)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            Get in Touch
          </Button>
        </Box>
      </Container>

      {/* Large Video Modal */}
      <VideoModal
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
        }}
      >
        <Fade in={modalOpen}>
          <ModalContent>
            <CloseButton onClick={handleCloseModal}>
              <Close />
            </CloseButton>
            
            {selectedVideo && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{ fontSize: '2rem' }}>{selectedVideo.flag}</Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: COLORS.text.primary }}>
                      {selectedVideo.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.text.secondary }}>
                      {selectedVideo.description} • {selectedVideo.context}
                    </Typography>
                  </Box>
                </Box>
                
                <ModalVideo 
                  controls
                  autoPlay
                  loop
                  preload="metadata"
                >
                  <source src={selectedVideo.videoUrl} type="video/mp4" />
                  <Typography sx={{ p: 4, textAlign: 'center', color: COLORS.text.secondary }}>
                    Video placeholder - Add your MBS speech videos to /public/demo/mbs/
                  </Typography>
                </ModalVideo>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.text.primary, mb: 1 }}>
                    Key Features:
                  </Typography>
                  <Grid container spacing={1}>
                    {selectedVideo.features.map((feature, idx) => (
                      <Grid item xs={12} sm={6} md={4} key={idx}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CheckCircle sx={{ fontSize: 16, color: '#006C35', mr: 1 }} />
                          <Typography variant="body2" sx={{ color: COLORS.text.secondary }}>
                            {feature}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </>
            )}
          </ModalContent>
        </Fade>
      </VideoModal>
    </Box>
  );
};

export default MBSDemoPage;